import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle } from 'lucide-react';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  videoType?: 'youtube' | 'loom';
  title?: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ 
  isOpen, 
  onClose, 
  videoId, 
  videoType = "youtube",
  title = "Video Player" 
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
    setHasError(false);
  };

  const handleVideoError = () => {
    setHasError(true);
    setIsVideoLoaded(false);
  };

  const getVideoEmbedUrl = () => {
    if (videoType === 'loom') {
      return `https://www.loom.com/embed/${videoId}?autoplay=1&muted=0&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`;
    }
    // YouTube
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&muted=0&rel=0&modestbranding=1&origin=${window.location.origin}`;
  };

  const getDirectVideoUrl = () => {
    if (videoType === 'loom') {
      return `https://www.loom.com/share/${videoId}`;
    }
    // YouTube
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const getPlatformName = () => {
    return videoType === 'loom' ? 'Loom.com' : 'YouTube';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleBackdropClick}
        >
          {/* Transparent backdrop with blur */}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
          
          {/* Modal content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-4xl bg-transparent"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 z-10 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Video container */}
            <div className="relative bg-transparent rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                {!isVideoLoaded && !hasError && (
                  <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
                    <div className="text-center text-white">
                      <div className="w-16 h-16 mx-auto mb-4 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-lg font-medium">Loading video...</p>
                    </div>
                  </div>
                )}

                {hasError && (
                  <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
                    <div className="text-center text-white p-8">
                      <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
                      <h3 className="text-xl font-semibold mb-4">Video Unavailable</h3>
                      <p className="text-gray-300 mb-6 max-w-md">
                        The video cannot be loaded directly. This is common in development environments due to security restrictions.
                      </p>
                      <div className="space-y-3">
                        <a
                          href={getDirectVideoUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                          Watch on {getPlatformName()}
                        </a>
                        <button
                          onClick={() => {
                            setHasError(false);
                            setIsVideoLoaded(false);
                          }}
                          className="block w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <iframe
                  src={getVideoEmbedUrl()}
                  title={title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  onLoad={handleVideoLoad}
                  onError={handleVideoError}
                />
              </div>
            </div>

            {/* Title */}
            {title && (
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center text-white text-xl font-semibold mt-4 drop-shadow-lg"
              >
                {title}
              </motion.h3>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;
